import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useParams } from 'react-router-dom'

import { CenterFull, Check, OpenNew, SidePeekRight, SidePeekRightFilled } from '@/assets/icons'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { IconButton } from '@/components/icon-button/IconButton'
import { kTooltipHoverDelayMs } from '@/constants/constants-ui'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { kRouteSegments } from '@/routing/paths'
import { useAppDispatch } from '@/store/store-hooks'
import { useDraftWorkspace } from '../hooks/useDraftWorkspace'
import { DraftWorkspaceActions } from '../store/slice'

type ChatViewMenuProps = {
  conversationId: string
}

/** View options for a conversation beside an open draft. */
export default function ChatViewMenu({ conversationId }: ChatViewMenuProps) {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const { draftArtifactVersionId, closeDraft } = useDraftWorkspace()

  if (!draftArtifactVersionId) return null

  const openInNewTab = () => {
    if (!matterId) return
    window.open(buildConversationPath(matterId, conversationId, kRouteSegments.agentV2), '_blank', 'noopener,noreferrer')
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton
          variant="plain"
          color="neutral"
          size="md"
          icon={<SidePeekRight />}
          aria-label="Switch view"
          tooltipText="Switch view"
          tooltipPlacement="bottom"
          tooltipHoverDelayMs={kTooltipHoverDelayMs}
          disableTooltipInteractive
          disableTooltipFocusListener
        />
      </DropdownMenu.Trigger>
      <MenuContent className="min-w-[220px]" sideOffset={5} align="end">
        <MenuItemGroup>
          {/* The switcher only shows in a docked chat panel, so right peek is always the current view. */}
          <MenuItem
            label="Right peek"
            icon={SidePeekRightFilled}
            variant="neutral"
            trailingIcon={Check}
            onSelect={() => {
              dispatch(DraftWorkspaceActions.setChatPanelOpen({ draftArtifactVersionId, open: true }))
            }}
          />
          {/* Full screen closes the adjacent work product and returns to the normal chat. */}
          <MenuItem label="Full screen" icon={CenterFull} variant="neutral" onSelect={closeDraft} />
          <MenuItem label="New tab" icon={OpenNew} variant="neutral" onSelect={openInNewTab} />
        </MenuItemGroup>
      </MenuContent>
    </DropdownMenu.Root>
  )
}
